import { useEffect, useRef } from 'react';
import { useSimStore } from '../../store/useSimStore';
import { generateVentBuffer, PAW_FULL_SCALE, VOL_FULL_SCALE } from '../../engine/ventEngine';
import styles from './VentWaveform.module.css';

const SAMPLE_RATE = 250;

/**
 * Pressure–Volume loop (Vol mL on Y, Paw cmH₂O on X) — the Aisys spirometry box.
 * Plots one breath cycle parametrically from the Paw and Volume waveform buffers.
 */
export default function VentLoop() {
  const canvasRef = useRef(null);
  const ventSettings = useSimStore((s) => s.ventSettings);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const draw = () => {
      const dpr = window.devicePixelRatio || 1;
      const r = canvas.getBoundingClientRect();
      if (!r.width || !r.height) return;   // not laid out yet — observer will retrigger
      canvas.width = Math.round(r.width * dpr);
      canvas.height = Math.round(r.height * dpr);
      const ctx = canvas.getContext('2d');
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
      const w = r.width, h = r.height;

      // One full breath cycle of paw + volume
      const period = 60 / Math.max(1, ventSettings.rr);
      const paw = generateVentBuffer('paw', ventSettings, SAMPLE_RATE, period);
      const vol = generateVentBuffer('volume', ventSettings, SAMPLE_RATE, period);
      const N = Math.min(paw.length, vol.length);

      ctx.fillStyle = '#0b0f14';
      ctx.fillRect(0, 0, w, h);

      // Axes
      const padL = 8, padB = 8, padT = 4, padR = 4;
      const plotW = w - padL - padR, plotH = h - padT - padB;
      ctx.strokeStyle = 'rgba(120,140,160,0.4)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(padL, padT); ctx.lineTo(padL, h - padB); ctx.lineTo(w - padR, h - padB);
      ctx.stroke();

      // Paw axis spans 0..PAW_FULL_SCALE/2 (≈ 0..20 like the reference), Vol 0..600
      const pawMax = PAW_FULL_SCALE / 2;
      const volMax = 600 / VOL_FULL_SCALE; // normalised vol full scale for plot
      ctx.strokeStyle = '#F2E63D';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      for (let k = 0; k < N; k++) {
        const pawN = (paw[k] * PAW_FULL_SCALE) / pawMax;   // 0..1 over 0..pawMax
        const volN = vol[k] / volMax;                       // 0..1 over 0..600
        const x = padL + Math.max(0, Math.min(1, pawN)) * plotW;
        const y = (h - padB) - Math.max(0, Math.min(1, volN)) * plotH;
        if (k === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.stroke();
    };

    draw();
    // Redraw when the box is first laid out, shown (container query), or resized.
    const observer = new ResizeObserver(draw);
    observer.observe(canvas);
    return () => observer.disconnect();
  }, [ventSettings]);

  return <canvas ref={canvasRef} className={styles.canvas} />;
}
